import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../app/i18n';

const LANGUAGES = [
  { code: 'en',  label: 'English',  short: 'EN',  flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  { code: 'tw',  label: 'Twi',      short: 'TW',  flag: '\uD83C\uDDEC\uD83C\uDDED' },
  { code: 'gaa', label: 'Ga',       short: 'GA',  flag: '\uD83C\uDDEC\uD83C\uDDED' },
  { code: 'ee',  label: 'Ewe',      short: 'EE',  flag: '\uD83C\uDDEC\uD83C\uDDED' },
] as const;

type Variant = 'light' | 'dark';

interface LanguageSwitcherProps {
  /** `dark` (default) for white-ish surfaces (dashboard topbar). `light` for dark surfaces (marketing nav). */
  variant?: Variant;
  /** Compact icon-only trigger (just the globe). Useful in very tight topbars. */
  compact?: boolean;
}

export default function LanguageSwitcher({ variant = 'dark', compact = false }: LanguageSwitcherProps) {
  const { t, i18n: i18nHook } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const current = LANGUAGES.find((l) => l.code === i18nHook.language) ?? LANGUAGES[0];

  const triggerStyles: React.CSSProperties = variant === 'light'
    ? {
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.25)',
        color: 'white',
      }
    : {
        background: 'white',
        border: '1px solid var(--heka-gray-200)',
        color: 'var(--heka-gray-700)',
      };

  const choose = (code: string) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('heka.lang', code); } catch { /* noop */ }
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`heka-langsw heka-langsw-${variant}`} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className="heka-langsw-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        aria-label={t('common.language') || 'Language'}
        title={t('common.language') || 'Language'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: compact ? '0' : '6px 10px',
          width: compact ? 36 : undefined,
          height: compact ? 36 : undefined,
          justifyContent: 'center',
          borderRadius: compact ? 10 : 999,
          fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
          cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
          ...triggerStyles,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>{'\uD83C\uDF10'}</span>
        {!compact && <span style={{ letterSpacing: 0.4 }}>{current.short}</span>}
        {!compact && <span aria-hidden="true" style={{ fontSize: 9, marginLeft: 2, opacity: 0.8 }}>{'\u25BE'}</span>}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('common.language') || 'Language'}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 160,
            background: 'white',
            color: 'var(--heka-gray-900)',
            border: '1px solid var(--heka-gray-200)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
            padding: 6,
            zIndex: 90,
          }}
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === i18nHook.language;
            const activeStr: 'true' | 'false' = active ? 'true' : 'false';
            return (
              <div
                key={lang.code}
                role="option"
                aria-selected={activeStr}
                tabIndex={0}
                onClick={() => choose(lang.code)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(lang.code); } }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: active ? 'var(--heka-primary-50)' : 'white',
                  color: active ? 'var(--heka-primary-700)' : 'var(--heka-gray-800)',
                  fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: 'var(--font-body)',
                  userSelect: 'none',
                }}
              >
                <span aria-hidden="true">{lang.flag}</span>
                <span style={{ flex: 1 }}>{lang.label}</span>
                {active && <span aria-hidden="true">{'\u2713'}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
